import { FactorLensDataType, FactorLensFocus } from "@/interfaces/factorLens.interface"
import { FactorContrib, FactorPortContrib } from "@/interfaces/raDashboard.interface"
import { FactorLensDataSet } from "@/services/report-dashboard/raDashboard.data.service"
import React, { createContext, useCallback, useMemo, useState } from "react"
import { useFactorLens } from "../RADashboardDataContext/hooks"

export interface FactorLensTopBottom {
  topItems: FactorContrib[],
  bottomItems: FactorContrib[]
}

const EMPTY_SECURITIES: FactorPortContrib[] = [];

export interface FactorLensContextData {
  allFactorData: Record<FactorLensDataType, FactorLensDataSet>,
  portfolioSecurities: FactorPortContrib[],
  selectedDataType: FactorLensDataType,
  setDataType: (factorType: FactorLensDataType) => void,
  selectedFactorBox: FactorLensFocus | null,
  setFactorBox: (focus: FactorLensFocus) => void,
}

const emptyTopBottom: FactorLensTopBottom = {
  topItems: [],
  bottomItems: []
}

const emptyFactorLensDataSet: FactorLensDataSet = {
  factors: [],
  levels: { 0: [], 1: [], 2: [], 3: [], 4: [] , 5: [] }
};

const emptyFactorData: Record<FactorLensDataType, FactorLensDataSet> = {
  [FactorLensDataType.Portfolio]: { ...emptyFactorLensDataSet },
  [FactorLensDataType.Benchmark]: { ...emptyFactorLensDataSet },
  [FactorLensDataType.Active]: { ...emptyFactorLensDataSet }
}

const initialContext: FactorLensContextData = {
  allFactorData: emptyFactorData,
  portfolioSecurities: [],
  selectedDataType: FactorLensDataType.Portfolio,
  setDataType: () => {},
  selectedFactorBox: null,
  setFactorBox: () => {},
};

export const FactorLensContext = createContext<FactorLensContextData>(initialContext);

export default function FactorLensProvider(props: React.PropsWithChildren) {
  const factorLensData = useFactorLens();
  const [viewedFactor, setViewedFactor] = useState<FactorLensDataType>(FactorLensDataType.Portfolio);
  const [factorFocus, setFactorFocus] = useState<FactorLensFocus | null>(null);
  const portfolioSecurities = factorLensData?.portfolioSecurities ?? EMPTY_SECURITIES;
  const allFactorData = useMemo(() => {
    if (factorLensData != null) {
      return {
        [FactorLensDataType.Portfolio]: factorLensData.portfolio,
        [FactorLensDataType.Benchmark]: factorLensData.benchmark,
        [FactorLensDataType.Active]: factorLensData.active
      }
    };
    return emptyFactorData;
  }, [factorLensData]);
  const setFactorBox = useCallback((focus: FactorLensFocus | null) => {
    setFactorFocus(focus);
  }, []);
  const contextValue: FactorLensContextData = {
    allFactorData,
    portfolioSecurities,
    selectedDataType: viewedFactor,
    selectedFactorBox: factorFocus,
    setDataType: (dataType) => setViewedFactor(dataType),
    setFactorBox
  };
  return <FactorLensContext.Provider value={contextValue}>
    {props.children}
  </FactorLensContext.Provider>
}